// src/components/Home.js

import React, { useState, useEffect, useCallback } from 'react';
// *** ADDED: Import PropTypes ***
import PropTypes from 'prop-types';
import {
    FaIdCard, FaUsers, FaUniversity, FaFileInvoice, FaCartPlus,
    FaGlobe, FaSpinner,
    FaExclamationTriangle
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import apiClient from '../api/apiClient';
import { useAuth } from '../context/AuthContext';

// --- Import Child Components ---
import ServiceDetailModal from './ServiceDetailModal';
import ViewServiceButton from './ViewServiceButton'; // Keep using this as styles are passed

// --- LocalStorage Key ---
const LAST_SELECTED_STATE_KEY = 'homeLastSelectedState';

// --- Helper Function to get Icon (Unchanged) ---
const getServiceIcon = (category) => {
    const iconProps = { size: 30 };
    const lowerCategory = category?.toLowerCase() || '';
    if (lowerCategory.includes('certificate') || lowerCategory.includes('id card') || lowerCategory.includes('govt id')) return <FaIdCard {...iconProps} />;
    if (lowerCategory.includes('scholarship') || lowerCategory.includes('education')) return <FaUniversity {...iconProps} />;
    if (lowerCategory.includes('welfare') || lowerCategory.includes('assistance')) return <FaUsers {...iconProps} />;
    if (lowerCategory.includes('proof') || lowerCategory.includes('invoice')) return <FaFileInvoice {...iconProps} />;
    return <FaFileInvoice {...iconProps} />;
};


// --- Main Home Component ---
// *** UPDATED: Accept 'onCartUpdate' prop ***
const Home = ({ onCartUpdate }) => {
    // Auth Hook (Unchanged)
    const { user, loading: isAuthLoading, error: authError } = useAuth();
    const isAuthenticated = !!user;

    // Component State (Unchanged)
    const [states, setStates] = useState([]);
    const [selectedState, setSelectedState] = useState('');
    const [stateServices, setStateServices] = useState([]);
    const [nationwideServices, setNationwideServices] = useState([]);
    const [isLoadingStates, setIsLoadingStates] = useState(false);
    const [isLoadingStateServices, setIsLoadingStateServices] = useState(false);
    const [isLoadingNationwide, setIsLoadingNationwide] = useState(false);
    const [errorStates, setErrorStates] = useState(null);
    const [errorStateServices, setErrorStateServices] = useState(null);
    const [errorNationwide, setErrorNationwide] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedServiceIdForModal, setSelectedServiceIdForModal] = useState(null);

    // --- Effects (Unchanged Logic) ---
    useEffect(() => {
        if (user) {
            const savedState = localStorage.getItem(LAST_SELECTED_STATE_KEY);
            if (savedState) setSelectedState(savedState); else setSelectedState('');
            const fetchInitialData = async () => {
                 setIsLoadingStates(true); setIsLoadingNationwide(true);
                 setErrorStates(null); setErrorNationwide(null);
                 try {
                     const results = await Promise.allSettled([
                         apiClient.get('/states'),
                         apiClient.get('/services?state=nationwide')
                     ]);
                     if (results[0].status === 'fulfilled') { setStates(results[0].value.data); }
                     else { console.error("States fetch error:", results[0].reason); setErrorStates(results[0].reason.response?.data?.error || 'Failed to load states'); }
                     if (results[1].status === 'fulfilled') { setNationwideServices(results[1].value.data); }
                     else { console.error("Nationwide fetch error:", results[1].reason); setErrorNationwide(results[1].reason.response?.data?.error || 'Failed to load nationwide services'); }
                 } catch (err) { console.error("Initial fetch error:", err); setErrorStates('An unexpected error occurred loading initial data.'); }
                 finally { setIsLoadingStates(false); setIsLoadingNationwide(false); }
            };
            fetchInitialData();
        } else {
             setStates([]); setNationwideServices([]); setStateServices([]); setSelectedState('');
             localStorage.removeItem(LAST_SELECTED_STATE_KEY);
             setIsLoadingStates(false); setIsLoadingNationwide(false);
             setErrorStates(null); setErrorNationwide(null); setErrorStateServices(null);
        }
    }, [user]);

    useEffect(() => {
        if (selectedState && selectedState !== '' && user) {
            const fetchStateServices = async () => {
                setIsLoadingStateServices(true); setErrorStateServices(null); setStateServices([]);
                try { const response = await apiClient.get(`/services?state=${selectedState}`); setStateServices(response.data); }
                catch (err) { console.error("State services fetch error:", err); setErrorStateServices('Failed to load services.'); }
                finally { setIsLoadingStateServices(false); }
            };
            fetchStateServices();
        } else { setStateServices([]); }
    }, [selectedState, user]);

    useEffect(() => {
        if (user && selectedState && selectedState !== '') localStorage.setItem(LAST_SELECTED_STATE_KEY, selectedState);
        else if (user && selectedState === '') localStorage.removeItem(LAST_SELECTED_STATE_KEY);
    }, [selectedState, user]);


    // --- Event Handlers ---
    const handleStateChange = (e) => { setSelectedState(e.target.value); setErrorStateServices(null); };
    const handleViewDetailsClick = (serviceId) => { setSelectedServiceIdForModal(serviceId); setIsModalOpen(true); };
    const handleCloseModal = () => { setIsModalOpen(false); setSelectedServiceIdForModal(null); };

    // *** UPDATED: Call 'onCartUpdate' callback ***
    const handleAddToCart = async (service) => {
        if (!service?.id) return;
         try {
             const response = await apiClient.post('/cart', { service_id: service.id });
             if (response.data?.message?.includes('already in cart')) {
                 toast.info(`"${service.name}" is already in your cart.`);
             } else {
                 toast.success(`"${service.name}" added to cart!`);
             }

             // Keep the storage event trigger (for potential other tabs)
             localStorage.setItem('cartUpdated', Date.now().toString());
             console.log('[Home] Cart updated event triggered via localStorage.');

             // --- ADDED: Call the passed-in callback function ---
             if (onCartUpdate) {
                 console.log('[Home] Calling direct onCartUpdate callback.');
                 onCartUpdate(); // Trigger the refresh directly
             } else {
                 console.warn('[Home] onCartUpdate prop not received.'); // Warning if prop is missing
             }
             // --- END ADDED ---

         } catch (err) {
             console.error("Home: Error adding to cart:", err);
             toast.error(err.response?.data?.error || 'Failed to add item to cart.');
         }
    };
    // *** END UPDATED handleAddToCart ***

    // --- Render Helpers (Unchanged) ---
    const renderLoading = (text = "Loading...") => (
        <div style={styles.loadingIndicator}>
            <FaSpinner style={{ ...styles.spinnerIcon, ...styles.spinnerAnimation }} />
            {text}
        </div>
    );
    const renderError = (errorText) => (
        <div style={styles.errorIndicator}>
            <FaExclamationTriangle style={styles.errorIcon} />
            Error: {errorText || 'Unknown error'}
        </div>
    );

    // --- Render Service Card (Unchanged) ---
    const renderServiceCard = (service) => (
        <div
            key={service.id}
            style={styles.card}
            onMouseEnter={(e) => { Object.assign(e.currentTarget.style, styles.cardHover); }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = styles.card.transform || 'none';
                e.currentTarget.style.boxShadow = styles.card.boxShadow;
            }}
        >
            <div style={styles.cardContent}>
                <div style={styles.iconWrapper}>{getServiceIcon(service.category)}</div>
                <h3 style={styles.serviceName}>{service.name}</h3>
                <p style={styles.servicePrice}>{service.price > 0 ? `₹${service.price}` : 'Free'}</p>
            </div>
            <div style={styles.btnGroup}>
                <ViewServiceButton
                    onClick={() => handleViewDetailsClick(service.id)}
                    baseStyle={styles.viewBtn}
                    hoverStyle={styles.viewBtnHover}
                />
                <button
                    style={styles.addBtn}
                    onClick={() => handleAddToCart(service)}
                    onMouseEnter={(e) => { Object.assign(e.currentTarget.style, styles.addBtnHover); }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = styles.addBtn.backgroundColor;
                        e.currentTarget.style.borderColor = styles.addBtn.borderColor || 'transparent';
                    }}
                    onFocus={(e) => { Object.assign(e.currentTarget.style, styles.btnFocus); }}
                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                >
                    <FaCartPlus style={{ marginRight: '0.4rem' }}/> Apply
                </button>
            </div>
        </div>
    );


    // --- Main Return Logic (Unchanged) ---
    if (isAuthLoading) return renderLoading("Initializing...");
    if (authError) return ( <div style={styles.centeredMessageContainer}> <h2>Authentication Error</h2> {renderError(authError)} <p>Please try logging in again.</p> </div> );
    if (!user) return ( <div style={styles.centeredMessageContainer}> <h2>Welcome!</h2> <p>Please log in to access the service portal.</p> </div> );

    // --- Render Logged-in View (Unchanged) ---
    return (
        <div style={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>

            <div style={styles.contentWrapper}>
                 <header style={styles.pageHeader}>
                      <div style={styles.welcomeMessage}>Welcome, {user.name}!</div>
                      <h1 style={styles.title}>Service Portal</h1>
                 </header>

                 <div style={styles.dropdownContainer}>
                     <label htmlFor="stateSelect" style={styles.dropdownLabel}>Select Your State:</label>
                     {isLoadingStates && renderLoading("Loading states...")}
                     {!isLoadingStates && !errorStates && states.length > 0 && (
                        <select
                            id="stateSelect"
                            style={styles.dropdown}
                            value={selectedState}
                            onChange={handleStateChange}
                            onFocus={(e) => { Object.assign(e.currentTarget.style, styles.dropdownFocus); }}
                            onBlur={(e) => {
                                e.currentTarget.style.borderColor = styles.dropdown.borderColor;
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                             <option value="">-- Select State --</option>
                             {states.map((state) => (<option key={state} value={state}>{state}</option>))}
                         </select>
                     )}
                     {errorStates && !isLoadingStates && renderError(errorStates)}
                     {!isLoadingStates && !errorStates && states.length === 0 && (<p style={styles.noDataMessage}>No states available.</p>)}
                 </div>

                 {selectedState && (
                    <section style={styles.section}>
                        <h2 style={styles.sectionTitle}>
                            <FaIdCard style={styles.sectionTitleIcon} /> Services in {selectedState}
                        </h2>
                        {isLoadingStateServices && renderLoading(`Loading services for ${selectedState}...`)}
                        {errorStateServices && !isLoadingStateServices && renderError(errorStateServices)}
                        {!isLoadingStateServices && !errorStateServices && (
                            stateServices.length > 0
                             ? ( <div style={styles.grid}> {stateServices.map(renderServiceCard)} </div>)
                             : ( <p style={styles.noDataMessage}>No specific services found for {selectedState}.</p> )
                        )}
                     </section>
                )}

                 <section style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <FaGlobe style={styles.sectionTitleIcon} /> Nationwide Services
                    </h2>
                    {isLoadingNationwide && renderLoading("Loading nationwide services...")}
                    {errorNationwide && !isLoadingNationwide && renderError(errorNationwide)}
                    {!isLoadingNationwide && !errorNationwide && (
                        nationwideServices.length > 0
                        ? ( <div style={styles.grid}> {nationwideServices.map(renderServiceCard)} </div>)
                        : (<p style={styles.noDataMessage}>No nationwide services available.</p>)
                    )}
                 </section>

            </div> {/* End Content Wrapper */}

             <ServiceDetailModal
                 isOpen={isModalOpen}
                 onClose={handleCloseModal}
                 serviceId={selectedServiceIdForModal}
             />

        </div> // End Container
    );
};

// --- Enhanced Styles Object (Unchanged) ---
const styles = {
    container: { height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif", backgroundColor: '#f8f9fa', color: '#343a40', },
    contentWrapper: { flex: 1, overflowY: 'auto', padding: '1.5rem clamp(1.5rem, 5vw, 4rem) 5rem', WebkitOverflowScrolling: 'touch', },
    pageHeader: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6', gap: '1rem', },
    title: { fontSize: 'clamp(1.8rem, 4vw, 2.2rem)', color: '#212529', fontWeight: 600, margin: 0, textAlign: 'center', flex: 1, minWidth: '200px', },
    welcomeMessage: { fontSize: '0.95rem', color: '#6c757d', textAlign: 'left', whiteSpace: 'nowrap', },
    dropdownContainer: { margin: '1rem auto 3rem auto', maxWidth: '600px', width: '100%', },
    dropdownLabel: { display: 'block', fontSize: '1rem', color: '#495057', marginBottom: '0.6rem', textAlign: 'left', fontWeight: 500, },
    dropdown: { width: '100%', padding: '0.8rem 1.2rem', fontSize: '1rem', lineHeight: 1.5, color: '#495057', backgroundColor: '#fff', backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1.2rem center', backgroundSize: '16px 12px', border: '1px solid #ced4da', borderRadius: '8px', appearance: 'none', cursor: 'pointer', transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out', },
    dropdownFocus: { borderColor: '#86b7fe', outline: '0', boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)', },
    section: { marginBottom: '3.5rem', },
    sectionTitle: { fontSize: 'clamp(1.5rem, 3vw, 1.8rem)', fontWeight: 600, color: '#343a40', display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #e9ecef', paddingBottom: '0.8rem', },
    sectionTitleIcon: { marginRight: '0.1rem', },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '2rem', },
    card: { backgroundColor: '#ffffff', borderRadius: '12px', padding: '1.8rem', textAlign: 'center', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05), 0 6px 20px rgba(0, 0, 0, 0.04)', border: '1px solid #e9ecef', transition: 'transform 0.25s ease, box-shadow 0.25s ease', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '270px', transform: 'translateY(0px)', },
    cardHover: { transform: 'translateY(-6px)', boxShadow: '0 8px 16px rgba(0, 0, 0, 0.07), 0 12px 30px rgba(0, 0, 0, 0.06)', },
    cardContent: { flexGrow: 1, marginBottom: '1.5rem', },
    iconWrapper: { marginBottom: '1.2rem', color: '#0d6efd', height: 'auto', lineHeight: 1, },
    serviceName: { fontSize: '1.2rem', fontWeight: 600, color: '#212529', marginBottom: '0.8rem', minHeight: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', },
    servicePrice: { fontSize: '1.05rem', color: '#0d6efd', fontWeight: 500, marginBottom: '0', },
    btnGroup: { display: 'flex', gap: '1rem', marginTop: 'auto', },
    baseBtn: { flex: 1, borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.95rem', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', border: '1px solid transparent', textAlign: 'center', whiteSpace: 'nowrap', transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.15s ease-in-out', },
    viewBtn: { flex: 1, borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.95rem', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', textAlign: 'center', whiteSpace: 'nowrap', transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.15s ease-in-out', backgroundColor: '#e9ecef', color: '#212529', borderColor: '#ced4da', },
    viewBtnHover: { backgroundColor: '#dee2e6', borderColor: '#adb5bd', },
    addBtn: { flex: 1, borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.95rem', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', textAlign: 'center', whiteSpace: 'nowrap', transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.15s ease-in-out', backgroundColor: '#0d6efd', color: '#ffffff', borderColor: '#0d6efd', borderWidth: '1px', borderStyle: 'solid', },
    addBtnHover: { backgroundColor: '#0b5ed7', borderColor: '#0a58ca', },
    btnFocus: { outline: '0', boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)', },
    loadingIndicator: { textAlign: 'center', padding: '1.5rem', color: '#6c757d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', gap: '0.7rem', borderRadius: '8px', },
    spinnerIcon: {},
    spinnerAnimation: { animation: 'spin 1s linear infinite', },
    errorIndicator: { textAlign: 'center', padding: '1rem 1.25rem', margin: '1rem 0', color: '#842029', backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', },
    errorIcon: {},
    noDataMessage: { textAlign: 'center', padding: '2.5rem', color: '#6c757d', fontStyle: 'italic', fontSize: '1.05rem', borderRadius: '8px', },
    centeredMessageContainer: { padding: '3rem 1.5rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, },
};

// *** ADDED: PropTypes and DefaultProps for Home component ***
Home.propTypes = {
  onCartUpdate: PropTypes.func, // Optional function prop
};

Home.defaultProps = {
    onCartUpdate: null, // Default to null (or () => {} if preferred)
}
// *** END ADDED ***

export default Home;